import { Injectable, inject } from '@angular/core'

import { EMPTY, Observable } from 'rxjs'
import { catchError, map } from 'rxjs/operators'

import { GridSearchService } from '@flowx/designer/shared/services/grid-search.service'
import {
  PaginatedEndpoint,
  PaginatedResponse,
  Sort,
} from '@flowx/designer/shared/services/paginated-datasource'
import { ToastMessageService } from '@flowx/designer/shared/services/toast-message.service'

import { UrlParametersStoreService } from './url-parameters-store.service'

import { PaginationQueryParams } from '../api/api-http.service'
import { RuntimeApiService, UiFlowSessionResponse } from '../api/runtime.api'
import { mapResource } from '../entities/application.entity'
import { UiFlowSession } from '../entities/ui-flow-sessions.types'
import { toRuntimeListFilterParams } from '../entities/utils'

@Injectable({
  providedIn: 'root',
})
export class UiFlowSessionsService extends GridSearchService<UiFlowSession> {
  #runtimeApi = inject(RuntimeApiService)
  #urlParametersStore = inject(UrlParametersStoreService)
  #toastMessageService = inject(ToastMessageService)

  getGridEndpointInfo(): {
    endpoint: PaginatedEndpoint<any, UiFlowSession>
    sort: Sort
    query?: Partial<any>
  } {
    return {
      endpoint: this.#getUiFlowSessionsPaginated.bind(this),
      sort: {
        // Backend expects sorting by `dateStart` (query param `sort=dateStarted,<direction>`)
        property: 'dateStarted',
        direction: 'desc',
      },
      query: {},
    }
  }

  getUiFlowSessions(
    pageable: PaginationQueryParams,
    query?: Partial<any>
  ): Observable<PaginatedResponse<UiFlowSession>> {
    const { appId } = this.#urlParametersStore.urlParameters
    return this.#runtimeApi.getUiFlowSessions({
      applicationId: appId,
      ...pageable,
      ...toRuntimeListFilterParams(query, 'status', 'statuses'),
    })
  }

  getUiFlowSession(id: string): Observable<UiFlowSessionResponse> {
    return this.#runtimeApi.getUiFlowSessionById(id).pipe(
      map((response) => mapResource(response)),
      catchError((err) => {
        this.#toastMessageService.showErrorMessage(
          err?.error?.title || 'Error loading UI Flow Session'
        )
        return EMPTY
      })
    )
  }

  #getUiFlowSessionsPaginated(
    pageable: PaginationQueryParams,
    query?: Partial<any>
  ): Observable<PaginatedResponse<UiFlowSession>> {
    return this.getUiFlowSessions(pageable, query)
  }
}
